import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { ComponentProps, useEffect, useRef, useState } from "react"
import { ResponsiveImageFragment } from "../../types.graphql"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import { animate, AnimatePresence, motion, useMotionValue } from "framer-motion"
import { twMerge } from "tailwind-merge"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"

type Props = {
  items?: null | (ResponsiveImageFragment | null | undefined)[]
  className?: {
    viewport?: string
    track?: string
    slide?: string
    thumbs?: string
  }
}

export function MediaGallery(props: Props) {
  const mediaItems = props.items?.filter(Boolean) as ResponsiveImageFragment[]

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({
      stopOnInteraction: true,
      delay: 8000,
    }),
    WheelGesturesPlugin(),
  ])

  const [selected, setSelected] = useState(0)
  useEffect(() => {
    if (!emblaApi) return
    const listen = emblaApi.on("select", (e) => {
      setSelected(e.selectedScrollSnap())
    })
    return () => listen.clear()
  }, [emblaApi])

  if (!mediaItems?.length) return null

  const activeItem = props.items?.[selected]

  return (
    <div>
      {/* Carousel viewport */}
      <div
        data-part="carousel-viewport"
        className={twMerge("overflow-hidden w-full", props.className?.viewport)}
        ref={emblaRef}
      >
        <div className={twMerge("flex pointer-events[pan-y_pinch-zoom] gap-4", props.className?.track)}>
          {mediaItems.map((item, index) => {
            return (
              <div
                className={twMerge(
                  "will-change-transform flex-none w-full h-full flex-shrink-0 flex-grow-0 self-stretch relative box-border",
                  props.className?.slide,
                )}
                key={index}
              >
                <ResponsiveImage
                  {...item}
                  key={index}
                  objectFit="contain"
                  objectPosition="center"
                  className="w-full h-full"
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className="h-[2.2em] pt-3 text-center type-ui-s-roman text-grey-mid flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={selected}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            dangerouslySetInnerHTML={{ __html: activeItem?.caption! }}
          ></motion.div>
        </AnimatePresence>
      </div>
      {/* Caption area */}
      <div
        data-part="carousel-thumbs"
        className={twMerge("stack-x-2 justify-center h-5 text-grey", props.className?.thumbs)}
      >
        {mediaItems.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              emblaApi?.scrollTo(index)
            }}
            className={twMerge("h-full opacity-25 transition-opacity", index === selected && "opacity-100")}
          >
            <ResponsiveImage {...item} key={index} objectFit="contain" objectPosition="center" className="h-full" />
          </button>
        ))}
      </div>
    </div>
  )
}

function AutoHeightAnimator(props: ComponentProps<"div">) {
  const ref = useRef<HTMLDivElement>(null)
  const height = useMotionValue(0)

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        animate(height, entry.contentRect.height)
      }
    })
    if (ref.current) ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  return (
    <motion.div style={{ height: height }}>
      <div ref={ref} {...props} />
    </motion.div>
  )
}
